import { APIError, fetcher } from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import { showToast } from "@certn/cascadia";
import { CascadiaJsonForm, CascadiaJsonFormContent } from "@certn/forms";
import { CascadiaFormSubmissionError } from "@certn/forms/src/errors";
import {
  getCoveredCountriesConsideringUKNations,
  useTimeTracking,
} from "@certn/utils";
import { PropsWithChildren, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddressIncompleteModal } from "./AddressIncompleteModal";
import { useApplication } from "./ApplicationContext";
import { useApplicationFormContext } from "./ApplicationFormContext";
import { ApplicationSummary } from "./ApplicationSummary";
import { renderers } from "./renderers";
import { isApplicationSchema } from "./types";
import {
  getCaseDetailEndpoint,
  getFirstIncompleteEnabledSection,
  getNextSection,
  parseSubmitErrors,
} from "./utils";

const HAS_RESIDENCE_PREFIX = "has_residence_in_";

interface Props extends PropsWithChildren {}

export function ApplicationForm(props: Props) {
  const {
    currentSection,
    uischema: applicationSchema,
    schema,
    data,
    saveData,
    navigateToSection,
    onChange,
    locale,
    completedSections,
    summaryComponent,
    basePath,
  } = useApplication();
  const { caseId } = useApplicationFormContext();

  const { startTimeTracker, cancelTimeTracker, stopTimeTracker } =
    useTimeTracking();

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [missingRequirements, setMissingRequirements] = useState<string[]>([]);

  useEffect(() => {
    startTimeTracker("application--section", currentSection);

    return () => {
      cancelTimeTracker("application--section", currentSection);
    };
  }, [startTimeTracker, cancelTimeTracker, currentSection]);

  const { t } = useTranslation();

  const uischema = currentSection
    ? applicationSchema?.elements.find(
        (element) => element.name === currentSection
      )
    : undefined;

  if (currentSection === "summary") {
    const SummaryComponent = summaryComponent || ApplicationSummary;
    // Only pass the text props if SummaryComponent accepts them
    // The local override in applicant-portal will handle passing them
    return <SummaryComponent />;
  }

  const caseEndpoint = getCaseDetailEndpoint(basePath, caseId);

  const loadNextSection = async () => {
    const { ui_schema, applicant_form_data } =
      await fetcher.request<components["schemas"]["CaseDetailResponse"]>(
        caseEndpoint
      );
    if (isApplicationSchema(ui_schema)) {
      const nextSection = getNextSection(
        currentSection,
        ui_schema,
        applicant_form_data
      );
      return nextSection?.name ? nextSection.name : undefined;
    }
    return undefined;
  };

  return (
    <>
      <CascadiaJsonForm
        key={currentSection}
        initialValues={data}
        schema={schema}
        uischema={uischema}
        formID={currentSection}
        locale={locale}
        onSubmit={async (values) => {
          const formValues = values as {
            has_not_lived_in_required_country?: boolean;
            residences?: Array<{
              start_date?: string;
              end_date?: string | null;
              address?: {
                country?: string;
                administrative_division_1?: string;
              };
            }>;
          };

          if (
            currentSection === "address-history" &&
            !formValues.has_not_lived_in_required_country
          ) {
            const residencesSchema = (
              schema as {
                properties?: {
                  residences?: {
                    errorMessage?: Record<string, string>;
                    nzCurrentRegionMinMonths?: number;
                  };
                };
              }
            )?.properties?.residences;
            const errorMessages: Record<string, string> =
              residencesSchema?.errorMessage ?? {};
            const residences = formValues.residences ?? [];
            const coveredCountries = new Set(
              getCoveredCountriesConsideringUKNations(residences)
            );

            const unsatisfied = Object.entries(errorMessages)
              .filter(([key]) => key.startsWith(HAS_RESIDENCE_PREFIX))
              .filter(([key]) => {
                const code = key
                  .slice(HAS_RESIDENCE_PREFIX.length)
                  .toUpperCase();
                return !coveredCountries.has(code);
              })
              .map(([, message]) => message);

            if (unsatisfied.length > 0) {
              setMissingRequirements(unsatisfied);
              setAddressModalOpen(true);
              return;
            }
          }

          try {
            await saveData(values);
            stopTimeTracker("application--section", currentSection);
            const nextSection = await loadNextSection();
            const firstIncompleteEnabledSection =
              await getFirstIncompleteEnabledSection(
                caseId,
                completedSections,
                currentSection,
                caseEndpoint
              );
            if (nextSection) {
              navigateToSection(nextSection);
            } else if (firstIncompleteEnabledSection) {
              navigateToSection(firstIncompleteEnabledSection);
            } else {
              navigateToSection("summary");
            }
          } catch (e) {
            if (e instanceof APIError && e.status === 400) {
              const errors = parseSubmitErrors(e) || {};

              const errorMessages = Object.values(errors)
                .flat()
                .filter((msg): msg is string => typeof msg === "string");

              if (errorMessages.length > 0) {
                errorMessages.forEach((msg) =>
                  showToast(msg, { type: "error" })
                );
              } else {
                showToast(t("There were errors in the form"), {
                  type: "error",
                });
              }

              throw new CascadiaFormSubmissionError(errors);
            }

            showToast(t("Something went wrong submitting the form"), {
              type: "error",
            });
            // Throw to signal failure to CascadiaForm so it does NOT clear the
            // draft. Without this, a transient 5xx/network error during
            // section save silently destroys the user's in-progress data.
            // Use CascadiaFormSubmissionError with empty errors: this
            // preserves the draft (CascadiaForm's catch returns early on
            // CascadiaFormSubmissionError) without surfacing per-field
            // errors that don't exist for non-400 responses.
            throw new CascadiaFormSubmissionError({});
          }
        }}
        onChange={onChange}
        renderers={renderers}
      >
        <CascadiaJsonFormContent />
      </CascadiaJsonForm>
      <AddressIncompleteModal
        isOpen={addressModalOpen}
        missingRequirements={missingRequirements}
        onAcknowledge={() => setAddressModalOpen(false)}
      />
    </>
  );
}
